import { __ } from '@wordpress/i18n';
import {
	Panel,
	TextControl,
	RangeControl,
	Button,
	Flex
} from '@wordpress/components';
import { PluginSidebar } from '@wordpress/edit-site';
import {
	PluginSidebar as PostPluginSidebar,
	PluginSidebarMoreMenuItem,
} from '@wordpress/edit-post';
import { registerPlugin } from '@wordpress/plugins';
import { withDispatch, withSelect } from '@wordpress/data';
import { compose, withState } from '@wordpress/compose';
import { check, update } from '@wordpress/icons';
import apiFetch from '@wordpress/api-fetch';
import { pluginIcon } from "./icons";

interface pluginOptions {
	aiBlock: {
		apiKey: string,
		apiKeyStatus: string,
		maxTokens: number,
	}
}

interface pluginProps {
	options: pluginOptions,
	setOptions: ( values: pluginOptions ) => void,
	response: boolean,
	setState: ( values: { [key: string]: any } ) => void,
}

const sidebarProps = {
	name: 'ai-block',
	title: 'AI Block',
	icon: pluginIcon,
};

const ApiKeyLabel = ( { valid }: { valid: boolean } ) => {
	return (
		<>
            <span
				className={ 'api-key-status is-' + ( valid ? 'valid' : 'invalid' ) }
				style={ {
					marginLeft: '10px',
					padding: '2px',
					color: valid ? '#03b365' : 'inherit',
					fontWeight: '500'
				} }
			>
				{ valid ? __( 'Valid', 'ai-block' ) : __( 'Inactive', 'ai-block' ) }
			</span>
		</>
	);
};

const Plugin = compose(
	withSelect( ( select ) => {
		return {
			options: select( 'core' )?.getEditedEntityRecord( 'root', 'site' ) ?? {},
		};
	} ),
	withDispatch( ( dispatch ) => {
		return {
			setOptions: ( values: { [key: string]: any } ) => {
				dispatch( 'core' )?.editEntityRecord( 'root', 'site', undefined, values );
			}
		}
	} ),
	withState( {
		response: false,
	} ),
)( ( { options, setOptions, response, setState }: pluginProps ): JSX.Element => {
		const isSiteEditor = document.body.classList.contains( 'site-editor-php' );
		const apiKey       = options?.aiBlock?.apiKey ?? '';
		const apiKeyStatus = options?.aiBlock?.apiKeyStatus ?? 'invalid';
		const SidebarTag   = ( props: object ) => {
			return isSiteEditor ? PluginSidebar( props ) : PostPluginSidebar( props );
		};

		async function checkApiKey() {
			setOptions( {
				aiBlock: {
					...( options?.aiBlock ?? {} ),
					apiKeyStatus: 'loading',
				}
			} );

			const response: unknown = await apiFetch( {
				path: '/ai-block/v1/completions/?prompt=check&max_tokens=2&api_key=' + encodeURI( apiKey ),
				method: 'GET'
			} ).then( response => response ).catch( error => error );

			const status = response === 'valid' ? 'valid' : 'invalid';

			setOptions( {
				aiBlock: {
					...( options?.aiBlock ?? {} ),
					apiKeyStatus: status,
				}
			} );

			if (status === 'valid') {
				setState( {
					response: true,
				} );
			}
		}

		return (
			<>
				<PluginSidebarMoreMenuItem target={ 'ai-block' }>
					{ __( 'AI Block', 'ai-block' ) }
				</PluginSidebarMoreMenuItem>

				<SidebarTag { ...sidebarProps }>
					<Panel
						title={ __( 'API Key', 'ai-block' ) }
						className={ 'ai-block-settings' }
					>
						<br/>
						<p
							className={ 'ai-block-label' }
						>
							{ __( 'OpenAI API Key ', 'ai-block' ) }
							<ApiKeyLabel valid={ apiKeyStatus === 'valid' }/>
						</p>
						<Flex>
							<TextControl
								value={ options?.aiBlock?.apiKey }
								onChange={ ( value: string ) => setOptions( {
									aiBlock: {
										...( options?.aiBlock ?? {} ),
										apiKey: value,
										apiKeyStatus: 'invalid',
									}
								} ) }
								style={ {
									marginBottom: '0',
								} }
							/>
							<Button
								isSmall
								isPrimary={ apiKeyStatus !== 'valid' }
								isBusy={ apiKeyStatus === 'loading' }
								disabled={ apiKeyStatus === 'loading' || apiKeyStatus === 'valid' }
								onClick={ () => checkApiKey() }
								label={ __( 'Check', 'ai-block' ) }
								icon={ apiKeyStatus === 'valid' ? check : update }
							/>
						</Flex>
						{ apiKeyStatus !== 'valid' && (
							<p
								style={ {
									marginTop: '8px',
									fontSize: '12px',
									fontStyle: 'normal',
									color: 'rgb( 117, 117, 117 )',
									marginBottom: 0
								} }
							>
								{ __( 'Enter your OpenAI API key above. Get started with a free API key from ', 'ai-block' ) }
								<a
									href={ 'https://openai.com/api/' }
									target={ '_blank' }
									rel={ 'noreferrer' }
									style={ {
										display: 'block',
									} }
								>
									{ 'https://platform.openai.com/account/api-keys ↗' }
								</a>
							</p>
						) }
						{ response &&
						  <p
							  style={ {
								  marginTop: '8px',
								  fontSize: '12px',
								  fontStyle: 'normal',
								  color: 'rgb( 117, 117, 117 )',
								  marginBottom: 0
							  } }
						  >
							  { __( 'API Key successfully validated. Please save your changes by clicking Update at the top of the page.', 'ai-block' ) }
						  </p>
						}
					</Panel>

					<Panel
						className={ 'ai-block-settings' }
					>
						<RangeControl
							label={ __( 'Max Tokens', 'ai-block' ) }
							help={ __( 'The maximum number of tokens used to generate a response. The default is 25 (approximately 100 characters).', 'ai-block' ) }
							spinControls={ 'custom' }
							isShiftStepEnabled={ true }
							value={ options?.aiBlock?.maxTokens ?? 25 }
							onChange={ ( value: number ) => setOptions( {
								aiBlock: {
									...( options?.aiBlock ?? {} ),
									maxTokens: value,
								}
							} ) }
							shiftStep={ 10 }
							step={ 1 }
							min={ 2 }
							max={ 250 }
							allowReset={ true }
						/>
					</Panel>
				</SidebarTag>
			</>
		);
	}
);

registerPlugin( 'ai-block', {
	icon: sidebarProps.icon,
	render: Plugin
} );
